import { ArrayFields, BUTTERFLY_SENTINEL, Card, formatSSN, FormattedMessageDiv, FormInput, FormWrapper, LoadingAnimation, Navigation, scrubSentinels, useWizardContext } from '@odmonorepo/core-components';
import { formatDateInput, validateDateComplete, validateDateFormat, formatToISODate, formatFromISODate, validateNotFuture } from '@odmonorepo/odfunctions';
import { Col, Row, Stack, Form } from 'react-bootstrap';
import { useFormContext } from 'react-hook-form';
import * as yup from 'yup';
import { patchAppDataOpts, useApplication } from 'merchant-api/unified';
import { Application, Person } from 'merchant-api/types';
import * as rfc6902 from 'rfc6902';
import { ensureSingleItem } from '../businessFormHelpers';
import { AddressInputSchema, AddressInput } from './AddressInput';
import { useMutation } from '@tanstack/react-query';
import { decide, optimizelyInstance } from 'merchant-api/vendor/optimizely';
import { useEffect, useState } from 'react';
import { useAnalytics } from 'use-analytics';
import { trackField, isValidSSN } from 'merchant-components';
import { hasProvePrefillConsent } from '../helpers';

const SSN = 'SOCIAL_SECURITY_NUMBER';

export const OwnerContactSchema = (showName: boolean, originalSSN?: string) =>
  yup.object({
    ...(showName && {
      firstName: yup.string().required(),
      lastName: yup.string().required(),
    }),
    addresses: yup.array().of(AddressInputSchema),
    dateOfBirth: yup.string()
      .required('formInput.dateOfBirth.error')
      .test('must be complete date', 'formInput.dateOfBirth.error', validateDateComplete)
      .test('must be valid date', 'formInput.dateOfBirth.error.invalidFormat', validateDateFormat)
      .test('must be in past', 'formInput.dateOfBirth.error.futureDate', validateNotFuture),
    identifiers: yup.array().of(
      yup.object({
        value: yup.string().when('type', {
          is: SSN,
          then: (schema) =>
            schema.required().test('ssn-validation', 'Social Security Number is invalid', function (value) {
              if (!value) return false;
              if (value === originalSSN) return true;
              if (/x/i.test(value)) return false;
              return isValidSSN(value);
            }),
          otherwise: (schema) => schema,
        }),
      }),
    ),
  });

const formatData = (data: Application) => {
  const formattedData = JSON.parse(JSON.stringify(data.data));
  formattedData.party.persons = ensureSingleItem(formattedData?.party?.persons, { identifiers: [{ type: SSN }], addresses: [{ type: 'PHYSICAL_ADDRESS' }] });

  if (formattedData.party.persons[0].dateOfBirth) {
    formattedData.party.persons[0].dateOfBirth = formatFromISODate(formattedData.party.persons[0].dateOfBirth) || '';
  } else {
    formattedData.party.persons[0].dateOfBirth = '';
  }

  formattedData.party.persons[0].identifiers = formattedData.party.persons[0].identifiers || [];
  formattedData.party.persons[0].addresses = formattedData.party.persons[0].addresses || [];

  let ssnIndex = formattedData.party.persons[0].identifiers?.findIndex((item) => item.type === SSN);
  if (ssnIndex === -1) {
    formattedData.party.persons[0].identifiers.push({
      type: SSN,
      value: ''
    });
    ssnIndex = formattedData.party.persons[0].identifiers.length - 1;
  }

  if (!formattedData.party.persons[0].addresses.some((item) => item.type === 'PHYSICAL_ADDRESS')) {
    formattedData.party.persons[0].addresses.push({
      type: 'PHYSICAL_ADDRESS',
      lineOne: '',
      lineTwo: '',
      city: '',
      state: '',
      postalCode: ''
    });
  }

  return {
    defaultValues: scrubSentinels({ ...formattedData.party.persons[0] }),
    ssnIndex,
  };
};

const hasName = (name?: string) => Boolean(name) && name !== BUTTERFLY_SENTINEL;

export const OwnerInfo: React.FC = () => {
  const { applicationData } = useApplication();

  if (!applicationData) return <Stack className="m-auto"><LoadingAnimation/></Stack>;
  const { ssnIndex, defaultValues } = formatData(applicationData);
  const persons = applicationData?.data?.party?.persons?.[0];
  const showName = hasProvePrefillConsent(applicationData) || !(hasName(persons?.firstName) && hasName(persons?.lastName));
  const originalSSN = persons?.identifiers?.find((id) => id.type === SSN)?.value;

  return (
    <FormWrapper schema={OwnerContactSchema(showName, originalSSN)} defaultValues={defaultValues}>
      <OwnerForm applicationData={applicationData} showName={showName} ssnIndex={ssnIndex} />
    </FormWrapper>
  );
};

type OwnerFormProps = {
  applicationData?: any;
  showName?: boolean;
  ssnIndex: number;
};

const OwnerForm = ({ applicationData, showName, ssnIndex }: OwnerFormProps) => {
  const { getValues, setValue } = useFormContext();
  const { nextStep, isFirstStep } = useWizardContext();
  const isOwnerStepFirst = isFirstStep();
  const analytics = useAnalytics();
  const [variation, setVariation] = useState<string>('');
  const [isSameBusinessAddress, setIsSameBusinessAddress] = useState(false);
  const businessAddress =
    applicationData?.data?.party?.businesses?.[0]?.addresses?.find(
      (addr: any) => addr.type === 'PHYSICAL_ADDRESS'
    );

  const { mutateAsync: patch } = useMutation({
    ...patchAppDataOpts(),
    onSuccess: () => nextStep(),
  });
  const isDirectNewApp = applicationData?.type === 'NEW' &&
    (applicationData?.metadata?.channelCode === 'DIRECT' || applicationData?.metadata?.channelCode === 'MARKETING' || applicationData?.metadata?.channelCode === 'PARTNER_REFERRAL')
  useEffect(() => {
    if (isDirectNewApp && optimizelyInstance) {
      const isStagingOrDev = import.meta.env?.MODE === 'staging' || import.meta.env?.MODE === 'development';
      const firstName = applicationData?.data?.party?.persons?.[0]?.firstName;
      const optimizelyAttrs = {
        ...(isStagingOrDev && firstName
          ? { firstName: firstName }
          : {})
      };

      decide('owner_address_autofill_test', optimizelyAttrs, analytics).then((result) => {
        setVariation(result || '');
      });
    }
  }, [analytics, applicationData, isDirectNewApp, optimizelyInstance]);

  const handleSameAsBusinessAddress = (checked: boolean) => {
    setIsSameBusinessAddress(checked);

    if (checked) {
      if (businessAddress) {
        setValue('addresses.0.lineOne', businessAddress.lineOne || '');
        setValue('addresses.0.lineTwo', businessAddress.lineTwo || '');
        setValue('addresses.0.city', businessAddress.city || '');
        setValue('addresses.0.state', businessAddress.state || '');
        setValue('addresses.0.postalCode', businessAddress.postalCode || '');
      }
    } else {
      setValue('addresses.0.lineOne', '');
      setValue('addresses.0.lineTwo', '');
      setValue('addresses.0.city', '');
      setValue('addresses.0.state', '');
      setValue('addresses.0.postalCode', '');
    }
  };

  const onNext = async () => {
    const updatedPerson = getValues();

    const personsArr = applicationData?.data?.party?.persons;
    let index = personsArr?.findIndex((person) => person.id === updatedPerson.id);
    const output = {
      ...applicationData?.data,
      party: {
        ...applicationData?.data?.party,
        persons: [...personsArr?.slice(0, index), updatedPerson, ...personsArr?.slice(index + 1)],
      },
    };
    if (updatedPerson?.dateOfBirth) {
      output.party.persons[index].dateOfBirth = formatToISODate(updatedPerson.dateOfBirth);
    }
    const patches = rfc6902.createPatch(applicationData?.data, output);
    await patch({ id: applicationData.id, patches });
  };

  return (
    <Stack gap={6} style={{ maxWidth: '600px', margin: 'auto' }}>
      <Stack gap={4}>
        <FormattedMessageDiv className="h3 text-center" id="renewalApp.step.2" />
        <FormattedMessageDiv className="h5 text-center" id="renewalApp.step.2.contactUs" values={{ tel: (txt: any) => <a href={`tel:(888)556-3483`}>{txt}</a> }} />
      </Stack>
      <Card key={0}>
        <Stack gap={5}>
          <Stack>
            <FormattedMessageDiv className="h4" id={'renewalApp.ownerInfo'} />
            <FormattedMessageDiv className="fs-6" id={'renewalApp.ownerInfo.description'} />
          </Stack>
          <ContactInfo
            showName={showName}
            variation={variation}
            isSameBusinessAddress={isSameBusinessAddress}
            onSameAsBusinessChange={handleSameAsBusinessAddress}
            businessAddress={businessAddress}
          />
          <SSNInfo ssnIndex={ssnIndex} />
          <Navigation stacked handleNext={onNext} continueOnly={isOwnerStepFirst} />
        </Stack>
      </Card>
      <FormattedMessageDiv className="fs-8" id="ownerInfo.disclaimer" values={{ sup: (text: string) => <sup>{text}</sup> }} />
    </Stack>
  );
};
type ContactInfoProps = {
  showName?: boolean;
  variation?: string;
  isSameBusinessAddress?: boolean;
  onSameAsBusinessChange?: (checked: boolean) => void;
  businessAddress?: {
    lineOne?: string;
    lineTwo?: string;
    city?: string;
    state?: string;
    postalCode?: string;
  };
};
const ContactInfo = ({
  showName,
  variation,
  isSameBusinessAddress = false,
  onSameAsBusinessChange,
  businessAddress,
}: ContactInfoProps) => {
  const showCheckbox = variation === 'same_address_checkbox' || variation === 'same_address_checkbox_with_label';
  const showAddressLabelVariation = variation === 'same_address_checkbox_with_label'; // This variation includes the address in the label regardless of checkbox state
  const showSameAddressVariation = variation === 'same_address_checkbox'; // This variation only shows the address in label when the checkbox is checked
  const addressLine1 = businessAddress?.lineOne;
  const addressLine2 = businessAddress?.lineTwo;
  const cityState = [businessAddress?.city, businessAddress?.state].filter(Boolean).join(', ');
  const cityStateZip = [cityState, businessAddress?.postalCode].filter(Boolean).join(' ');
  return (
    <>
      {showName && (
        <Row xs={1} md={2} className="g-5">
          <Col>
            <FormInput<Person> name={`firstName`} data-testid="firstName" controlProps={{ autoComplete: 'given-name' }} />
          </Col>
          <Col>
            <FormInput<Person> name={`lastName`} data-testid="lastName" controlProps={{ autoComplete: 'family-name' }} />
          </Col>
        </Row>
      )}

      {showCheckbox && (
        <Form.Check
          type="checkbox"
          id="sameAsBusinessAddress"
          name="sameAsBusinessAddress"
          label={
            <div>
              {showSameAddressVariation && (
                <>
                  {isSameBusinessAddress ? (
                    <FormattedMessageDiv id="ownerInfo.sameBusinessAddressWithAddress" />
                  ) : (
                    <FormattedMessageDiv id="ownerInfo.sameBusinessAddress" />
                  )}
                  {isSameBusinessAddress && addressLine1 && (
                    <div className="fw-semibold">{addressLine1}</div>
                  )}
                  {isSameBusinessAddress && addressLine2 && (
                    <div className="fw-semibold">{addressLine2}</div>
                  )}
                  {isSameBusinessAddress && cityStateZip && (
                    <div className="fw-semibold">{cityStateZip}</div>
                  )}
                </>
              )}
              {showAddressLabelVariation && (
                <>
                  <FormattedMessageDiv id="ownerInfo.sameBusinessAddressWithAddress" />
                  {addressLine1 && (
                    <div className="fw-semibold">{addressLine1}</div>
                  )}
                  {addressLine2 && (
                    <div className="fw-semibold">{addressLine2}</div>
                  )}
                  {cityStateZip && (
                    <div className="fw-semibold">{cityStateZip}</div>
                  )}
                </>
              )}
            </div>
          }
          checked={isSameBusinessAddress}
          onChange={(e) => onSameAsBusinessChange?.(e.target.checked)}
          onBlur={() => {
            trackField(
              undefined,
              'sameAsBusinessAddress',
              'Blur'
            );
          }}
          data-testid="sameAsBusinessAddressCheckbox"
        />
      )}

      {/* Hide AddressInput UX when checkbox is checked (variation: same_address_checkbox). */}
      {!isSameBusinessAddress && <AddressInput<Person> />}
      <FormInput<Person>
        name="dateOfBirth"
        data-testid="dateOfBirth"
        formatValue={formatDateInput}
        label={<FormattedMessageDiv id={`formInput.dateOfBirth`} />}
        helperText={<FormattedMessageDiv id="formInput.dateOfBirth.helperText" />}
        useYupErrors={true}
        controlProps={{ inputMode: 'numeric' }}
      />
    </>
  );
};

const SSNInfo = ({ ssnIndex }: { ssnIndex: number }) => (
  <>
    <Stack>
      <FormattedMessageDiv className="h4" id={'person.ssn.title'} />
      <FormattedMessageDiv
        className="fs-7"
        id="person.ssn.description"
        values={{
          sup: (txt: any) => {
            return <sup>{txt}</sup>;
          },
        }}
      />
    </Stack>
    <ArrayFields<Person> name={`identifiers`} data-testid="identifiers">
      {({ fields }) => {
        return (
          <FormInput<Person>
            className="fs-mask"
            name={`identifiers.${ssnIndex}.value`}
            data-testid="ssn"
            stripFormatOnSave
            formatValue={formatSSN}
            label={<FormattedMessageDiv id="formInput.ssn" />}
            controlProps={{
              inputMode: 'numeric',
              onFocus: (e: React.FocusEvent<HTMLInputElement>) => {
                setTimeout(() => e.target.select(), 0);
              },
              onClick: (e: React.MouseEvent<HTMLInputElement>) => {
                (e.target as HTMLInputElement).select();
              },
            }}
          />
        );
      }}
    </ArrayFields>
  </>
);

export default OwnerInfo;
